import {
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { Paper, Alert } from '@mantine/core';
import { useSensorData } from '@/hooks/useSensorData';
import { useChartHeight } from '@/hooks/useChartHeight';
import { useIsMobile } from '@/hooks/useIsMobile';
import { measurePerformance } from '@/utils/performance';
import { calculateNiceAxisTicks } from '@/utils/formatters';
import { useTimeAxis } from '@/hooks/useTimeAxis';
import { useEffect, useMemo, useRef } from 'react';
import { ChartSkeleton } from '@/components/charts/ChartSkeleton';
import { ChartHeader } from '@/components/charts/ChartHeader';
import { CHART_GRID_STROKE, CHART_AXIS_TICK, CHART_LEGEND_STYLE } from '@/utils/chartStyles';
import { DEVICE_COLORS, CHART_CROSSHAIR_COLOR } from '@/utils/chartColors';
import { ChartTooltipContent } from '@/components/charts/ChartTooltipContent';
import {
  ChartAnnotationLayer,
  filterAnnotationsForChart,
} from '@/components/charts/ChartAnnotationLayer';
import type { ChartAnnotation } from '@/types/chartAnnotation';

interface TemperatureChartProps {
  startDate: Date;
  endDate: Date;
  /** Pre-fetched sensor data from consolidated dashboard endpoint */
  sensorData?: Array<{ timestamp: string; temp: number | null }>;
  /** Downsampling info from consolidated endpoint */
  interval?: string;
  isDownsampled?: boolean;
  /** True when moving average is applied - sensor data uses timestamps directly */
  isAveraged?: boolean;
  /** Loading state from parent */
  isLoading?: boolean;
  /** Error from parent */
  error?: Error | null;
  expanded?: boolean;
  onToggle?: () => void;
  /** Annotations to display on the chart */
  annotations?: ChartAnnotation[];
  /** Callback when the "Add note" button is clicked */
  onAddNote?: () => void;
  /** Callback when an annotation is clicked for editing */
  onAnnotationClick?: (annotation: ChartAnnotation) => void;
}

export function TemperatureChart({
  startDate,
  endDate,
  sensorData,
  interval: parentInterval,
  isDownsampled: parentDownsampled,
  isAveraged: _isAveraged = false,  // Reserved for future use when grouping is needed
  isLoading: parentLoading,
  error: parentError,
  expanded,
  onToggle,
  annotations,
  onAddNote,
  onAnnotationClick,
}: TemperatureChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);

  // Responsive hooks
  const chartHeight = useChartHeight();
  const isMobile = useIsMobile();

  // Use hook as fallback when props not provided
  // Disable the hook query when parent provides data OR parent is loading
  const hookResult = useSensorData({
    startDate,
    endDate,
    parameters: ['temp'],
    enabled: sensorData === undefined && parentLoading !== true,
  });

  // Use parent data if provided, otherwise use hook data
  const rawData = sensorData ?? hookResult.data?.data;
  const isLoading = parentLoading ?? hookResult.isLoading;
  const error = parentError ?? hookResult.error;
  const isDownsampled = parentDownsampled ?? hookResult.data?.downsampled ?? false;
  const interval = parentInterval ?? hookResult.data?.interval;

  // Convert string timestamps to numbers for the numeric X-axis
  const chartData = useMemo(() => {
    if (!rawData) return [];
    return rawData.map(point => ({
      ...point,
      timestamp: new Date(point.timestamp).getTime(),
    }));
  }, [rawData]);

  // Range-aware X-axis: dates for multi-day, times for single-day
  const timeAxis = useTimeAxis(startDate, endDate);

  // Calculate Y-axis with nice ticks
  const yAxisConfig = useMemo(() => {
    if (!rawData || rawData.length === 0) {
      return calculateNiceAxisTicks(20, 30, 5, false);
    }

    const values = rawData
      .map(d => d.temp)
      .filter((v): v is number => v != null && typeof v === 'number' && !isNaN(v));

    if (values.length === 0) {
      return calculateNiceAxisTicks(20, 30, 5, false);
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    return calculateNiceAxisTicks(min, max, 5, false);
  }, [rawData]);

  // Measure chart render performance
  useEffect(() => {
    if (rawData && !isLoading) {
      const measure = measurePerformance('TemperatureChart');
      const timerId = setTimeout(() => measure.end(), 0);
      return () => clearTimeout(timerId);
    }
  }, [rawData, isLoading]);

  // Early returns AFTER all hooks
  if (isLoading) {
    return <ChartSkeleton title="Temperature" />;
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading temperature data">
        {error.message}
      </Alert>
    );
  }

  if (!rawData || rawData.length === 0) {
    return (
      <Alert color="blue" title="No data available">
        No temperature data found for the selected date range.
      </Alert>
    );
  }

  return (
    <Paper ref={chartRef} p={{ base: 'sm', md: 'lg' }} withBorder radius={12} bg="var(--rdh-card-bg)">
      <ChartHeader
        title="Temperature"
        subtitle={isMobile ? '(°C)' : (isDownsampled ? `(Downsampled to ${interval})` : undefined)}
        chartRef={chartRef}
        expanded={expanded}
        onToggle={onToggle}
        onAddNote={onAddNote}
      />

      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart data={chartData}>
          <defs>
            <linearGradient id="tempGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="var(--mantine-color-teal-6)" stopOpacity={0.3} />
              <stop offset="95%" stopColor="var(--mantine-color-teal-6)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />

          {/* Annotation regions - rendered behind data lines */}
          {annotations && (
            <ChartAnnotationLayer
              annotations={filterAnnotationsForChart(
                annotations,
                'temperature',
                ['apex']
              )}
              onAnnotationClick={onAnnotationClick}
            />
          )}

          <XAxis
            dataKey="timestamp"
            type="number"
            domain={[startDate.getTime(), new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1).getTime()]}
            ticks={timeAxis.ticks}
            tickFormatter={timeAxis.formatTick}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
            height={isMobile ? 35 : 50}
          />

          <YAxis
            domain={yAxisConfig.domain}
            ticks={yAxisConfig.ticks}
            tickFormatter={yAxisConfig.tickFormatter}
            width={isMobile ? 35 : 60}
            tick={CHART_AXIS_TICK}
            axisLine={false}
            tickLine={false}
          />

          {/* Crosshair tooltip with custom content */}
          <Tooltip
            cursor={{
              stroke: CHART_CROSSHAIR_COLOR,
              strokeWidth: 1,
              strokeDasharray: '4 4'
            }}
            content={
              <ChartTooltipContent
                valueFormatter={(v) => `${v.toFixed(2)}°C`}
              />
            }
          />

          <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={CHART_LEGEND_STYLE} />

          {/* Area fill under temperature line */}
          <Area
            type="linear"
            dataKey="temp"
            stroke="none"
            fill="url(#tempGrad)"
            fillOpacity={1}
            isAnimationActive={false}
            connectNulls={false}
            legendType="none"
          />

          {/* Main temperature line */}
          <Line
            type="linear"
            dataKey="temp"
            stroke={DEVICE_COLORS.apex}
            strokeWidth={1.5}
            dot={false}                     // No dots for performance
            name="Temperature"
            activeDot={{ r: 6 }}            // Dot at crosshair position
            connectNulls={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Paper>
  );
}
